import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Skeleton, Divider, notification, message, Typography, Empty } from 'antd';
import UserStore from 'stores/UserStore';
import RemoveConfirm from 'components/shared/RemoveConfirm';
import AddEditAddress from 'components/AddressActions/AddEdit/';
import UIStore from '@/stores/UIStore';
import ErrorRender from 'components/shared/ErrorRender';
import { IAddresses } from 'types/types';
import Verify from 'components/Verify';

const { Paragraph, Title, Text } = Typography;

type CountryLike =
  | string
  | number
  | null
  | undefined
  | { name?: string | number; code?: string | number; alpha3?: string | number };

const toStr = (v: unknown) => (v == null ? '' : String(v));

export const countryDisplay = (c?: CountryLike) => {
  if (c == null) return '';
  if (typeof c === 'string' || typeof c === 'number') return String(c);
  const name = toStr(c.name);
  const alpha3 = toStr(c.alpha3);
  const code = toStr(c.code);
  return name || alpha3 || code;
};

const AddressCard: React.FC<{
  item: IAddresses;
  onRemove: (id: number) => Promise<void>;
  onEdited: () => void;
}> = React.memo(({ item, onRemove, onEdited }) => {
  return (
    <Card.Grid
      key={item.id}
      hoverable={false}
      className="mx-1 mt-1"
      style={{ minWidth: 300, maxWidth: 420 }}
      data-testid={`address-card-${item.id}`}
    >
      <Title level={4} className="mb-2">
        {item.name}
      </Title>

      <address className="not-italic leading-6">
        <p className="text-base leading-none mb-1 text-gray-600">{item.street}</p>
        <p className="text-base leading-none mb-1 text-gray-600">
          {[item.city, item.state].filter(Boolean).join(', ')} {String(item.zip_code ?? '')}
        </p>
        <p className="text-base leading-none mb-3 text-gray-600">
          {countryDisplay(item.country)}
        </p>
      </address>

      <div className="flex justify-end gap-2">
        <RemoveConfirm name={item.name} record={item} handleRemove={async () => onRemove(item.id)} />
        <AddEditAddress updateData={onEdited} record={item} />
      </div>
    </Card.Grid>
  );
});
AddressCard.displayName = 'AddressCard';

const ManageAddresses: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IAddresses[]>([]);
  const mountedRef = useRef(true);
  const dataRef = useRef<IAddresses[]>([]);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await UserStore.getAddresses();
      if (!mountedRef.current) return;
      setData(res?.results ?? []);
    } catch (error) {
      notification.error({ message: 'Error', description: <ErrorRender error={error} /> });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    UIStore.setSelectedKey(['addresses']);
    UIStore.setDocumentTitle('Addresses');
    if (UserStore.isVerified) fetchAddresses();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAddresses]);

  const updateData = useCallback(async () => {
    await fetchAddresses();
  }, [fetchAddresses]);

  // optimistic remove with safe rollback using ref
  const handleRemove = useCallback(async (id: number) => {
    const prev = dataRef.current;
    setData((curr) => curr.filter((it) => it.id !== id));
    try {
      await UserStore.removeAddress(id);
      message.success('Successfully deleted');
    } catch (error) {
      setData(prev); // rollback to latest snapshot
      notification.error({ message: 'Error', description: <ErrorRender error={error} /> });
    }
  }, []);

  const isEmpty = useMemo(() => !loading && data.length === 0, [loading, data.length]);

  if (!UserStore.isVerified) return <Verify />;

  return (
    <Card title="Manage Addresses" bordered extra={<AddEditAddress updateData={updateData} />}>
      <Paragraph className="mb-2">
        Enter other addresses you need us to bill or ship to. They are selectable at the time of placing a Quote or an
        Order.
      </Paragraph>

      <Divider className="my-3" />

      {loading && <Skeleton paragraph={{ rows: 4 }} active />}

      {isEmpty && (
        <Empty
          description={
            <span>
              No addresses yet. Click <Text strong>Add Address</Text> to create one.
            </span>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {!loading && !isEmpty && (
        <div className="flex flex-wrap">
          {data.map((item) => (
            <AddressCard key={item.id} item={item} onRemove={handleRemove} onEdited={updateData} />
          ))}
        </div>
      )}
    </Card>
  );
};

export default ManageAddresses;
